/**
 * #427 W2 — manager approve / deny queue for time-off requests.
 *
 * Gated by the caller on `time-off:manage:all` (owner + manager).
 *
 * Design Philosophy principles served:
 *   P1 (one glance, ONE truth) — the pending BADGE and the pending LIST are
 *     derived from the SAME fetched array. There is deliberately no
 *     count endpoint: "if two numbers on a screen could disagree, they must
 *     come from the same source." `pending.length` IS the badge.
 *   P2 (the next action is obvious) — each pending row carries its two
 *     decisions inline; the review note is offered, never demanded (denying
 *     without a reason is allowed, but the box is right there).
 *   P4 (feeling fast) — skeleton rows while loading, never a spinner wall.
 *   P5 (never lie) — a request the server already decided (409 `not_pending`)
 *     is NOT re-decidable: decided requests render as history with their
 *     outcome, not as actionable rows, and a lost race re-syncs from the
 *     server instead of pretending the click won.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Skeleton } from '@ferro/ui';
import {
  ApiError,
  fetchTimeOff,
  readSession,
  reviewTimeOff,
  TIME_OFF_TYPE_LABELS,
  type TimeOffRequest,
  type TimeOffType,
} from '../../lib/api.js';
import { formatDayRange } from '../../lib/schedule-dates.js';

type QueueState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; requests: TimeOffRequest[] };

function typeLabel(type: string): string {
  return TIME_OFF_TYPE_LABELS[type as TimeOffType] ?? 'Time off';
}

/** Human outcome for a decided request — never the raw status enum. */
function outcomeLabel(status: string): string {
  switch (status) {
    case 'approved':
      return 'Approved';
    case 'denied':
      return 'Denied';
    case 'cancelled':
      return 'Withdrawn';
    default:
      return 'Pending';
  }
}

function outcomeBadgeClass(status: string): string {
  switch (status) {
    case 'approved':
      return 'ferro-badge ferro-badge--success';
    case 'denied':
      return 'ferro-badge ferro-badge--error';
    default:
      return 'ferro-badge ferro-badge--pending';
  }
}

export type TimeOffQueueProps = {
  readonly apiUrl: string;
};

export function TimeOffQueue({ apiUrl }: TimeOffQueueProps): JSX.Element {
  const [state, setState] = useState<QueueState>({ kind: 'loading' });
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    const { token } = readSession();
    if (!token) {
      setState({ kind: 'error', message: 'Your session expired. Sign in again.' });
      return;
    }
    try {
      // ONE read. Pending and history are two slices of the same array, so
      // the badge can never disagree with the list (P1).
      const { requests } = await fetchTimeOff(apiUrl, token);
      setState({ kind: 'ready', requests: requests ?? [] });
    } catch (err) {
      setState({
        kind: 'error',
        message:
          err instanceof Error
            ? err.message
            : 'We couldn’t load time-off requests.',
      });
    }
  }, [apiUrl]);

  useEffect(() => {
    void load();
  }, [load]);

  const pending = useMemo(
    () =>
      state.kind === 'ready'
        ? state.requests.filter((r) => r.status === 'pending')
        : [],
    [state],
  );
  const decided = useMemo(
    () =>
      state.kind === 'ready'
        ? state.requests.filter((r) => r.status !== 'pending')
        : [],
    [state],
  );

  const decide = async (
    request: TimeOffRequest,
    status: 'approved' | 'denied',
  ): Promise<void> => {
    const { token } = readSession();
    if (!token) return;
    setBusyId(request.id);
    setActionError(null);
    const reviewNote = (notes[request.id] ?? '').trim();
    try {
      const { request: updated } = await reviewTimeOff(apiUrl, token, request.id, {
        status,
        ...(reviewNote ? { reviewNote } : {}),
      });
      setState((curr) =>
        curr.kind === 'ready'
          ? {
              kind: 'ready',
              requests: curr.requests.map((r) =>
                r.id === updated.id ? updated : r,
              ),
            }
          : curr,
      );
      setNotes((curr) => {
        const next = { ...curr };
        delete next[request.id];
        return next;
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Someone else decided it first. Do NOT render our click as if it
        // won — re-read the truth from the server.
        setActionError(
          `${request.staff_name}’s request was already decided by someone else. Refreshed with the latest.`,
        );
        void load();
      } else {
        setActionError(
          err instanceof Error
            ? err.message
            : 'We couldn’t save that decision. Try again.',
        );
      }
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="ferro-schedule-section" aria-label="Time off requests">
      <h2 className="ferro-schedule-section__title">
        Time off
        {state.kind === 'ready' ? (
          <span
            className={`ferro-badge ${
              pending.length > 0 ? 'ferro-badge--pending' : 'ferro-badge--info'
            } ferro-timeoff-queue__badge`}
            data-testid="time-off-pending-badge"
          >
            {pending.length} waiting
          </span>
        ) : null}
      </h2>

      {state.kind === 'loading' ? (
        <div data-testid="time-off-queue-skeleton" aria-busy="true">
          <Skeleton variant="table-row" />
          <Skeleton variant="table-row" />
        </div>
      ) : null}

      {state.kind === 'error' ? (
        <div className="ferro-dashboard__error" role="alert">
          <p>{state.message}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => void load()}
          >
            Try again
          </button>
        </div>
      ) : null}

      {actionError ? (
        <div className="ferro-alert ferro-alert--warning" role="alert">
          {actionError}
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        <>
          {pending.length === 0 ? (
            <p className="ferro-muted" data-testid="time-off-queue-empty">
              Nobody is waiting on an answer. New requests land here for you to
              approve or deny.
            </p>
          ) : (
            <ul className="ferro-table" aria-label="Pending time off requests">
              {pending.map((r) => (
                <li
                  key={r.id}
                  className="ferro-table__row ferro-timeoff-queue__row"
                  data-testid="time-off-pending-row"
                >
                  <div className="ferro-table__body">
                    <div className="ferro-table__primary">{r.staff_name}</div>
                    <div className="ferro-table__meta">
                      {formatDayRange(r.start_date, r.end_date)} ·{' '}
                      {typeLabel(r.type)}
                    </div>
                    {r.note ? (
                      <p className="ferro-timeoff-queue__note">“{r.note}”</p>
                    ) : null}
                    <label className="ferro-field ferro-timeoff-queue__reviewnote">
                      <span className="ferro-field__label">
                        Note back to {r.staff_name.split(' ')[0]} (optional)
                      </span>
                      <input
                        type="text"
                        className="ferro-field__input"
                        value={notes[r.id] ?? ''}
                        onChange={(e) =>
                          setNotes((curr) => ({ ...curr, [r.id]: e.target.value }))
                        }
                        placeholder="Why — helps them plan"
                        data-testid={`time-off-review-note-${r.id}`}
                      />
                    </label>
                  </div>
                  <div className="ferro-table__actions">
                    <button
                      type="button"
                      className="ferro-btn ferro-btn--outline"
                      disabled={busyId === r.id}
                      aria-busy={busyId === r.id}
                      onClick={() => void decide(r, 'approved')}
                      data-testid={`time-off-approve-${r.id}`}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="ferro-btn ferro-btn--ghost"
                      disabled={busyId === r.id}
                      onClick={() => void decide(r, 'denied')}
                      data-testid={`time-off-deny-${r.id}`}
                    >
                      Deny
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {decided.length > 0 ? (
            <details className="ferro-timeoff-queue__history">
              <summary>Recently decided ({decided.length})</summary>
              <ul className="ferro-table" aria-label="Decided time off requests">
                {decided.map((r) => (
                  <li
                    key={r.id}
                    className="ferro-table__row"
                    data-testid="time-off-decided-row"
                  >
                    <div className="ferro-table__body">
                      <div className="ferro-table__primary">{r.staff_name}</div>
                      <div className="ferro-table__meta">
                        {formatDayRange(r.start_date, r.end_date)} ·{' '}
                        {typeLabel(r.type)}
                      </div>
                      {r.review_note ? (
                        <p className="ferro-timeoff-queue__note">
                          “{r.review_note}”
                        </p>
                      ) : null}
                    </div>
                    <div className="ferro-table__actions">
                      <span className={outcomeBadgeClass(r.status)}>
                        {outcomeLabel(r.status)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
